import React, { useMemo } from "react";

import "./customPagination.css";

interface CustomPaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  currentPage,
  totalPages,
  total,
  limit,
  hasNextPage,
  hasPrevPage,
  onPageChange,
  onLimitChange,
}) => {
  const pages = useMemo(() => {
    const items: (number | "...")[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
    } else {
      items.push(1);
      if (currentPage > 3) items.push("...");

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) items.push(i);

      if (currentPage < totalPages - 2) items.push("...");
      items.push(totalPages);
    }

    return items;
  }, [currentPage, totalPages]);

  const from = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const to = Math.min(currentPage * limit, total);

  return (
    <div className="custom-pagination">
      <span className="custom-pagination__info">
        Showing {from}–{to} of {total}
      </span>

      <div className="custom-pagination__limit">
        <span>Rows:</span>
        <select
          className="custom-pagination__select"
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <div className="custom-pagination__controls">
        <button
          className="custom-pagination__btn"
          disabled={!hasPrevPage}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Prev
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="custom-pagination__ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`custom-pagination__btn ${p === currentPage ? "custom-pagination__btn--active" : ""}`}
              onClick={() => onPageChange(p as number)}
            >
              {p}
            </button>
          ),
        )}
        <button
          className="custom-pagination__btn"
          disabled={!hasNextPage}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CustomPagination;
